// ============================================================================
// BESPOKIAN Health - PDMP Details Page
// Prescription Drug Monitoring Program results display
// ============================================================================

import { useState } from 'react';
import { useParams, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Shield,
  AlertTriangle,
  CheckCircle,
  XCircle,
  User,
  Building2,
  Calendar,
  Pill,
  FileText,
  Clock,
  ArrowLeft,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { queueApi } from '@/api/queue.api';
import { PDMPRiskLevel } from '@/types';
import { Button, Card, CardHeader, CardTitle, CardContent, Badge, Alert, Spinner } from '@/components/common/ui';
import { RiskGauge } from '@/components/pdmp/RiskGauge';
import { RiskFactors } from '@/components/pdmp/RiskFactors';
import { OverrideReasonModal } from '@/components/modals/OverrideReasonModal';

// ============================================================================
// Helper Functions
// ============================================================================

function getRiskLevelConfig(level: PDMPRiskLevel) {
  switch (level) {
    case 'CRITICAL':
      return {
        color: 'text-red-700',
        bgColor: 'bg-red-100',
        borderColor: 'border-red-300',
        label: 'Critical Risk',
        icon: <XCircle className="h-5 w-5 text-red-600" />,
      };
    case 'HIGH':
      return {
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        label: 'High Risk',
        icon: <AlertTriangle className="h-5 w-5 text-red-500" />,
      };
    case 'MODERATE':
      return {
        color: 'text-amber-600',
        bgColor: 'bg-amber-50',
        borderColor: 'border-amber-200',
        label: 'Moderate Risk',
        icon: <AlertTriangle className="h-5 w-5 text-amber-500" />,
      };
    case 'LOW':
    default:
      return {
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        label: 'Low Risk',
        icon: <CheckCircle className="h-5 w-5 text-green-500" />,
      };
  }
}

// ============================================================================
// Component
// ============================================================================

export function PDMPPage() {
  const { id } = useParams({ from: '/app/rx/$id/pdmp' });
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [showOverrideModal, setShowOverrideModal] = useState(false);

  // Fetch prescription details
  const { data: prescription, isLoading: prescriptionLoading } = useQuery({
    queryKey: ['prescription', id],
    queryFn: () => queueApi.getPrescription(id),
    enabled: !!id,
  });

  // Fetch PDMP results
  const { data: pdmpResult, isLoading: pdmpLoading, error } = useQuery({
    queryKey: ['pdmp', id],
    queryFn: () => queueApi.getPDMPResult(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minute cache
  });

  // Override mutation
  const overrideMutation = useMutation({
    mutationFn: (reason: string) => queueApi.overridePDMP(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pdmp', id] });
      queryClient.invalidateQueries({ queryKey: ['prescription', id] });
      toast.success('PDMP alert overridden with clinical justification');
      setShowOverrideModal(false);
      navigate({ to: `/rx/${id}/fill` });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to override PDMP alert');
    },
  });

  const isLoading = prescriptionLoading || pdmpLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !pdmpResult) {
    return (
      <div className="p-6">
        <Alert variant="danger">
          <AlertTriangle className="h-5 w-5" />
          <span>Failed to load PDMP results. Please try again.</span>
        </Alert>
        <Button
          variant="secondary"
          onClick={() => navigate({ to: '/queue' })}
          className="mt-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Queue
        </Button>
      </div>
    );
  }

  const riskConfig = getRiskLevelConfig(pdmpResult.riskLevel);
  const requiresOverride = pdmpResult.riskLevel === 'HIGH' || pdmpResult.riskLevel === 'CRITICAL';

  return (
    <div className="space-y-6 p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate({ to: '/queue' })}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">PDMP Report</h1>
            <p className="text-gray-600 mt-1">
              Prescription Drug Monitoring Program Results
            </p>
          </div>
        </div>
        <Badge className={`${riskConfig.bgColor} ${riskConfig.color} text-lg px-4 py-2`}>
          {riskConfig.icon}
          <span className="ml-2">{riskConfig.label}</span>
        </Badge>
      </div>

      {/* Patient Info */}
      {prescription && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Patient Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-gray-500">Patient Name</p>
                <p className="font-semibold text-lg">
                  {prescription.patient?.firstName} {prescription.patient?.lastName}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Date of Birth</p>
                <p className="font-semibold">
                  {prescription.patient?.dateOfBirth ? new Date(prescription.patient.dateOfBirth).toLocaleDateString() : 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Current Prescription</p>
                <p className="font-semibold">{prescription.drugName} {prescription.strength}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Risk Assessment */}
      <div className="grid grid-cols-2 gap-6">
        {/* Risk Gauge */}
        <Card className={`border-2 ${riskConfig.borderColor}`}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Risk Assessment
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center">
            <RiskGauge score={pdmpResult.riskScore} />
            <p className="text-sm text-gray-600 mt-4 text-center">
              Based on prescription history analysis
            </p>
          </CardContent>
        </Card>

        {/* Risk Factors */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Risk Factors
            </CardTitle>
          </CardHeader>
          <CardContent>
            <RiskFactors factors={pdmpResult.riskFactors} />
          </CardContent>
        </Card>
      </div>

      {/* PDMP Statistics */}
      <Card>
        <CardHeader>
          <CardTitle>Prescription History Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-6">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <User className="h-6 w-6 mx-auto text-gray-400 mb-2" />
              <p className="text-3xl font-bold text-gray-900">
                {pdmpResult.totalPrescribers}
              </p>
              <p className="text-sm text-gray-600">Unique Prescribers</p>
              <p className="text-xs text-gray-500">(Last 12 months)</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <Building2 className="h-6 w-6 mx-auto text-gray-400 mb-2" />
              <p className="text-3xl font-bold text-gray-900">
                {pdmpResult.totalPharmacies}
              </p>
              <p className="text-sm text-gray-600">Unique Pharmacies</p>
              <p className="text-xs text-gray-500">(Last 12 months)</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <Pill className="h-6 w-6 mx-auto text-gray-400 mb-2" />
              <p className="text-3xl font-bold text-gray-900">
                {pdmpResult.totalControlledRx}
              </p>
              <p className="text-sm text-gray-600">Controlled Rx</p>
              <p className="text-xs text-gray-500">(Last 12 months)</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <Calendar className="h-6 w-6 mx-auto text-gray-400 mb-2" />
              <p className="text-3xl font-bold text-gray-900">
                {pdmpResult.daysSinceLastControlled || 'N/A'}
              </p>
              <p className="text-sm text-gray-600">Days Since Last</p>
              <p className="text-xs text-gray-500">Controlled Fill</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Overlapping Prescriptions */}
      {pdmpResult.overlappingPrescriptions && pdmpResult.overlappingPrescriptions.length > 0 && (
        <Card className="border-amber-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="h-5 w-5" />
              Overlapping Controlled Prescriptions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pdmpResult.overlappingPrescriptions.map((rx, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-100"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900">{rx.drugName}</span>
                      <Badge variant="danger">{rx.deaSchedule}</Badge>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {rx.quantity} {rx.quantityUnit} • {rx.sigCode}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">{rx.prescriberName}</p>
                    <p className="text-sm text-gray-600">{rx.pharmacyName}</p>
                    <p className="text-xs text-gray-500">
                      Filled: {new Date(rx.filledDate).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Recent Controlled Substance History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pdmpResult.recentHistory && pdmpResult.recentHistory.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3 font-medium text-gray-600">Date</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-600">Drug</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-600">Schedule</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-600">Qty</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-600">Days Supply</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-600">Prescriber</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-600">Pharmacy</th>
                  </tr>
                </thead>
                <tbody>
                  {pdmpResult.recentHistory.map((entry, index) => (
                    <tr key={index} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="py-2 px-3">
                        {new Date(entry.filledDate).toLocaleDateString()}
                      </td>
                      <td className="py-2 px-3 font-medium">{entry.drugName}</td>
                      <td className="py-2 px-3">
                        <Badge variant="secondary">{entry.deaSchedule}</Badge>
                      </td>
                      <td className="py-2 px-3">{entry.quantity}</td>
                      <td className="py-2 px-3">{entry.daysSupply}</td>
                      <td className="py-2 px-3">{entry.prescriberName}</td>
                      <td className="py-2 px-3">{entry.pharmacyName}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">
              No recent controlled substance history found.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Clock className="h-4 w-4" />
          <span>
            Report generated: {new Date(pdmpResult.queriedAt).toLocaleString()}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            onClick={() => navigate({ to: '/queue' })}
          >
            Return to Queue
          </Button>
          {requiresOverride ? (
            <Button
              variant="danger"
              onClick={() => setShowOverrideModal(true)}
            >
              <AlertTriangle className="h-4 w-4 mr-2" />
              Override with Justification
            </Button>
          ) : (
            <Button
              variant="success"
              onClick={() => navigate({ to: `/rx/${id}/fill` })}
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Proceed to Fill
            </Button>
          )}
        </div>
      </div>

      {/* Override Modal */}
      <OverrideReasonModal
        isOpen={showOverrideModal}
        onClose={() => setShowOverrideModal(false)}
        onSubmit={(reason: string) => overrideMutation.mutate(reason)}
        title="Override PDMP Alert"
        description={`This patient has a ${riskConfig.label.toLowerCase()} PDMP score. Clinical justification is required to proceed.`}
        isSubmitting={overrideMutation.isPending}
      />
    </div>
  );
}
